import React, { useEffect, useId, useRef, useState } from "react";
import { GitBranch } from "lucide-react";
import {
  getRepo,
  preflightCompareSource,
  submitCompareSource,
} from "./api";
import { parseCompareSourceInput } from "./parseCompareSourceInput";
import RepositoryAnalysisConfirmation from "./RepositoryAnalysisConfirmation";
import { VIEWER_READ_ONLY_MESSAGE } from "./orgPermissions";
import {
  analysisModeLabel,
  formatCompareAnalysisCopy,
  formatPinLabel,
  formatSha,
  humanReuseReason,
  isFullSnapshotMode,
  isTargetedMode,
  primaryConfirmLabel,
} from "./sourceComparePreflightCopy";
import ProgressBar from "./ProgressBar";
import {
  buildAnalysisProgress,
  deriveAnalysisStage,
  formatAnalysisProgressCopy,
} from "./analysisProgress";

function getErrorPayload(err) {
  const data = err?.response?.data;
  return {
    message: data?.error || err?.message || "Could not add that source.",
    code: data?.code,
    offerNewRepository: Boolean(data?.offerNewRepository),
    resolved: data?.resolved,
  };
}

export default function SourceCompareAddSource({
  repoId,
  canAddSource = true,
  onOpenNewRepository,
  onSourceReady,
  persistedState = null,
  onStateChange,
  onJobStarted,
}) {
  const inputId = useId();
  const statusId = useId();
  const inputRef = useRef(null);
  const restored = persistedState || {};
  const [open, setOpen] = useState(Boolean(restored.open || restored.job || restored.preflight));
  const [sourceInput, setSourceInput] = useState(restored.sourceInput || "");
  const [phase, setPhase] = useState(restored.phase || "idle");
  const [error, setError] = useState(restored.error || null);
  const [preflight, setPreflight] = useState(restored.preflight || null);
  const [job, setJob] = useState(restored.job || null);
  const [liveRepo, setLiveRepo] = useState(null);
  const [showRelatedPlan, setShowRelatedPlan] = useState(false);
  const [showFullSnapshot, setShowFullSnapshot] = useState(false);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    if (!job?.repoId || (phase !== "queued" && phase !== "processing")) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const repo = await getRepo(job.repoId);
        if (cancelled) return;
        setLiveRepo(repo);
        if (repo.status === "completed") {
          setPhase("completed");
          onSourceReady?.({
            repoId: repo._id,
            commitSha: repo.analysisSource?.commitSha || repo.prIntake?.head?.sha,
            stale: Boolean(repo.prIntake?.stale),
            preflight,
          });
          return;
        }
        if (repo.status === "failed") {
          setPhase("failed");
          setError({
            message: repo.finalizeError || "Analysis failed. You can try again here.",
          });
          return;
        }
        setPhase(repo.status === "pending" ? "queued" : "processing");
      } catch (err) {
        if (!cancelled) {
          setError(getErrorPayload(err));
        }
      }
    };
    poll();
    const timer = setInterval(poll, 2500);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [job?.repoId, phase, onSourceReady, preflight]);

  useEffect(() => {
    onStateChange?.({
      open,
      sourceInput,
      phase,
      error,
      preflight,
      job,
    });
  }, [open, sourceInput, phase, error, preflight, job, onStateChange]);

  const reset = () => {
    setPhase("idle");
    setError(null);
    setPreflight(null);
    setJob(null);
    setLiveRepo(null);
    setShowRelatedPlan(false);
    setShowFullSnapshot(false);
  };

  const handlePreview = async (event) => {
    event?.preventDefault();
    const parsed = parseCompareSourceInput(sourceInput);
    if (!parsed.ok) {
      setError({ message: parsed.message });
      setPhase("idle");
      return;
    }
    setError(null);
    setPhase("resolving");
    try {
      const response = await preflightCompareSource(repoId, { sourceInput: sourceInput.trim() });
      setPreflight(response.preflight);
      setPhase("awaiting_confirmation");
    } catch (err) {
      setPhase("failed");
      setError(getErrorPayload(err));
    }
  };

  const handleConfirm = async (requestedMode) => {
    setError(null);
    setPhase("queued");
    try {
      const result = await submitCompareSource(repoId, {
        sourceInput: sourceInput.trim(),
        confirmed: true,
        requestedMode: requestedMode || preflight?.mode,
        quoteToken: preflight?.quoteToken,
      });
      setJob(result);
      onJobStarted?.(result);
      if (result.idempotent && result.status === "completed") {
        setPhase("completed");
        onSourceReady?.({
          repoId: result.repoId,
          commitSha: result.preflight?.headPin?.sha,
          stale: Boolean(result.preflight?.existingAnalysis?.stale),
          preflight: result.preflight,
        });
        return;
      }
      setPhase(result.status === "processing" ? "processing" : "queued");
    } catch (err) {
      setPhase("failed");
      setError(getErrorPayload(err));
    }
  };

  const analyzedCounts = liveRepo?.incrementalAnalysis?.counts || preflight?.counts || {};
  const plannedCount =
    liveRepo?.processableFiles ||
    analyzedCounts.modelAnalyzedFiles ||
    analyzedCounts.filesToAnalyze ||
    analyzedCounts.toAnalyze ||
    0;
  const relatedPlan =
    liveRepo?.incrementalAnalysis?.relatedFiles || preflight?.relatedFiles || [];
  const analysisProgress = buildAnalysisProgress({
    processedFiles: liveRepo?.processedFiles,
    processableFiles: plannedCount,
    plannedFiles: liveRepo?.prReusePlan || preflight?.files,
    relatedFiles: relatedPlan,
    structureFiles: liveRepo?.structure?.files,
  });
  const analysisStage = deriveAnalysisStage({
    status:
      phase === "completed"
        ? "completed"
        : phase === "failed"
          ? "failed"
          : liveRepo?.status || (phase === "queued" ? "pending" : "processing"),
    finalizeStatus: liveRepo?.finalizeStatus,
    progress: analysisProgress,
  });
  const statusLabel = {
    idle: "",
    resolving: "Resolving source…",
    awaiting_confirmation: "Awaiting confirmation. No credits have been used.",
    queued: "Queued for analysis…",
    processing:
      analysisStage === "finalizing"
        ? "File analysis finished. Building the repository map…"
        : "Analyzing the selected files…",
    completed: "Analysis complete. The other source is listed below.",
    failed: "Analysis did not finish.",
  }[phase];
  const showProgress = Boolean(
    preflight && (phase === "queued" || phase === "processing" || phase === "completed")
  );

  if (!canAddSource) {
    return (
      <p className="source-compare-muted" role="status">
        {VIEWER_READ_ONLY_MESSAGE}
      </p>
    );
  }

  return (
    <div className="source-compare-add">
      {!open ? (
        <button
          type="button"
          className="secondary-button source-compare-add-toggle"
          onClick={() => {
            setOpen(true);
            if (phase !== "queued" && phase !== "processing") {
              reset();
            }
          }}
        >
          <GitBranch size={16} aria-hidden="true" />
          Add branch or PR
        </button>
      ) : (
        <form className="source-compare-add-form" onSubmit={handlePreview}>
          <label htmlFor={inputId}>Branch or pull request</label>
          <input
            ref={inputRef}
            id={inputId}
            type="text"
            value={sourceInput}
            onChange={(event) => {
              setSourceInput(event.target.value);
              if (phase === "awaiting_confirmation") {
                setPhase("idle");
                setPreflight(null);
              }
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                setOpen(false);
                if (phase !== "queued" && phase !== "processing") {
                  reset();
                }
              }
            }}
            placeholder="branch-name, github.com/owner/repo/tree/branch, or a pull request URL"
            aria-describedby={statusId}
            disabled={phase === "resolving" || phase === "queued" || phase === "processing"}
          />
          <div className="source-compare-add-actions">
            <button
              type="submit"
              className="secondary-button"
              disabled={!sourceInput.trim() || phase === "resolving"}
            >
              {phase === "resolving" ? "Resolving…" : "Preview cost"}
            </button>
            <button
              type="button"
              className="source-compare-add-cancel"
              onClick={() => {
                setOpen(false);
                if (phase !== "queued" && phase !== "processing") {
                  reset();
                }
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div id={statusId} className="source-compare-status" role="status" aria-live="polite">
        {statusLabel}
        {preflight?.existingAnalysis?.stale && (
          <span> This source is stale because the pull request head moved.</span>
        )}
      </div>

      {showProgress && (
        <div className="source-compare-progress" data-testid="source-compare-progress">
          <h3>
            {phase === "completed" ? "Ready to compare" : "What is being analyzed"}
          </h3>
          <p>
            {analysisModeLabel(preflight.mode)} of{" "}
            <strong>{sourceInput || preflight.headPin?.ref || "this source"}</strong>
            {preflight.headPin?.sha ? ` at ${preflight.headPin.sha.slice(0, 12)}` : ""}.
          </p>
          <p data-testid="source-compare-progress-copy">
            {formatCompareAnalysisCopy(preflight)}
          </p>
          {analysisProgress.total > 0 && (
            <>
              <p data-testid="source-compare-progress-count">
                {phase === "queued" && analysisProgress.processedCount === 0
                  ? `${analysisProgress.total} file${analysisProgress.total === 1 ? "" : "s"} planned for analysis`
                  : formatAnalysisProgressCopy(analysisProgress, { stage: analysisStage })}
              </p>
              {(phase === "queued" || phase === "processing") && (
                <ProgressBar
                  processed={analysisProgress.processedCount}
                  total={analysisProgress.total}
                  compact
                  stage={analysisStage}
                />
              )}
            </>
          )}
          {analysisProgress.remainingPreview.length > 0 && phase !== "completed" && (
            <div data-testid="source-compare-progress-remaining">
              <h4>Still to analyze</h4>
              <ul>
                {analysisProgress.remainingPreview.map((path) => (
                  <li key={path}>{path}</li>
                ))}
              </ul>
              {analysisProgress.remainingOverflow > 0 && (
                <p className="source-compare-muted">
                  +{analysisProgress.remainingOverflow} more
                </p>
              )}
            </div>
          )}
          {analysisProgress.donePreview.length > 0 && (
            <div data-testid="source-compare-progress-done">
              <h4>Files analyzed</h4>
              <ul>
                {analysisProgress.donePreview.map((path) => (
                  <li key={path}>{path}</li>
                ))}
              </ul>
            </div>
          )}
          {relatedPlan.length > 0 && !analysisProgress.hasFileList && (
            <ul data-testid="source-compare-progress-related">
              {relatedPlan.slice(0, 8).map((file) => (
                <li key={`${file.path}:${file.reason}`}>
                  {file.path}
                  {file.reason ? ` — ${String(file.reason).replace(/_/g, " ")}` : ""}
                </li>
              ))}
            </ul>
          )}
          <p className="source-compare-muted">
            You can leave this tab. The review stays here, and the other source appears in
            the list below when it is ready.
          </p>
        </div>
      )}

      {error && (
        <div className="source-compare-error" role="alert">
          <span>{error.message}</span>
          {error.offerNewRepository && (
            <button
              type="button"
              className="secondary-button"
              onClick={() => onOpenNewRepository?.(sourceInput.trim())}
            >
              Open in New Repository
            </button>
          )}
          {phase === "failed" && (
            <button type="button" className="secondary-button" onClick={reset}>
              Try again
            </button>
          )}
        </div>
      )}

      {phase === "awaiting_confirmation" && preflight && (
        <div className="source-compare-preflight">
          <dl className="source-compare-preflight-meta">
            <div>
              <dt>Resolved source</dt>
              <dd>
                {preflight.headPin.owner}/{preflight.headPin.repo} ({preflight.kind}
                {preflight.isCrossFork ? ", cross-fork" : ""})
              </dd>
            </div>
            <div>
              <dt>Base</dt>
              <dd title={formatSha(preflight.basePin?.sha)?.full || undefined}>
                {formatPinLabel(preflight.basePin)}
              </dd>
            </div>
            <div>
              <dt>Head</dt>
              <dd title={formatSha(preflight.headPin?.sha)?.full || undefined}>
                {formatPinLabel(preflight.headPin)}
              </dd>
            </div>
            <div>
              <dt>Mode</dt>
              <dd data-testid="source-compare-mode">
                {analysisModeLabel(preflight.mode)}
              </dd>
            </div>
            <div>
              <dt>Changed files</dt>
              <dd data-testid="source-compare-changed-count">
                {preflight.counts?.changedFiles ?? 0}
                {preflight.counts?.changedFilesComplete === false ? " (incomplete)" : ""}
              </dd>
            </div>
            <div>
              <dt>Changed files to analyze</dt>
              <dd data-testid="source-compare-changed-analyze-count">
                {preflight.counts?.changedFilesRequiringAnalysis ??
                  (isTargetedMode(preflight.mode) ? preflight.counts?.changedFiles : preflight.counts?.filesToAnalyze) ??
                  0}
              </dd>
            </div>
            <div>
              <dt>Related files</dt>
              <dd data-testid="source-compare-related-count">
                {preflight.counts?.relatedFilesSelected ?? preflight.relatedFiles?.length ?? 0}
              </dd>
            </div>
            <div>
              <dt>Files to analyze</dt>
              <dd data-testid="source-compare-analyze-count">
                {preflight.counts?.modelAnalyzedFiles ??
                  preflight.counts?.filesToAnalyze ??
                  preflight.counts?.toAnalyze ??
                  0}
              </dd>
            </div>
            <div>
              <dt>Unchanged files reused</dt>
              <dd data-testid="source-compare-reuse-count">
                {preflight.counts?.unchangedReusable ?? 0}
              </dd>
            </div>
            <div>
              <dt>Excluded / classified</dt>
              <dd>{preflight.counts?.excludedClassified ?? 0}</dd>
            </div>
          </dl>
          <p className="source-compare-muted" data-testid="source-compare-diff-copy">
            {formatCompareAnalysisCopy(preflight)}
          </p>
          {!preflight.baseIdentityAvailable || !preflight.basePin?.sha ? (
            <p className="source-compare-warning" role="alert">
              The live base commit could not be resolved. Incremental reuse is refused until
              the base ref has an exact SHA.
            </p>
          ) : null}
          {isTargetedMode(preflight.mode) && (
            <p className="source-compare-warning" data-testid="source-compare-limitation">
              Limitation: {humanReuseReason(preflight.evidenceLimitations?.[0] || preflight.reason)}.{" "}
              {humanReuseReason("repository_wide_regression_unverified")}
            </p>
          )}
          {preflight.relatedFiles?.length > 0 && (
            <div>
              <button
                type="button"
                className="source-compare-add-cancel"
                onClick={() => setShowRelatedPlan((open) => !open)}
              >
                {showRelatedPlan ? "Hide related-file plan" : "View related-file plan"}
              </button>
              {showRelatedPlan && (
                <ul data-testid="source-compare-related-plan">
                  {preflight.relatedFiles.map((file) => (
                    <li key={`${file.path}:${file.reason}`}>
                      {file.path} — {file.reason.replace(/_/g, " ")}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {preflight.reason && !isTargetedMode(preflight.mode) && !isFullSnapshotMode(preflight.mode) && preflight.mode !== "verified_incremental" && preflight.mode !== "incremental" && (
            <p className="source-compare-muted" data-testid="source-compare-fallback-reason">
              Fallback reason: {humanReuseReason(preflight.reason)}
            </p>
          )}
          {preflight.legacyAdoption?.adopted && (
            <p className="source-compare-muted">
              Existing analysis can be upgraded safely ({preflight.legacyAdoption.method}).
            </p>
          )}
          {preflight.existingAnalysis?.status === "completed" && (
            <p className="source-compare-muted" role="status">
              This exact source is already analyzed. Confirm to select it without another charge.
            </p>
          )}
          <RepositoryAnalysisConfirmation
            estimate={preflight.estimate}
            prPreflight={preflight}
            confirmLabel={primaryConfirmLabel(preflight)}
            onConfirm={() => handleConfirm(preflight.mode)}
            onCancel={() => {
              setPhase("idle");
              setPreflight(null);
              setShowRelatedPlan(false);
              setShowFullSnapshot(false);
            }}
          />
          {preflight.fullSnapshotEstimate && !isFullSnapshotMode(preflight.mode) && (
            <div className="source-compare-full-snapshot">
              <button
                type="button"
                className="source-compare-add-cancel"
                onClick={() => setShowFullSnapshot((open) => !open)}
              >
                {showFullSnapshot ? "Hide verified full snapshot" : "Run verified full snapshot"}
              </button>
              {showFullSnapshot && (
                <RepositoryAnalysisConfirmation
                  estimate={preflight.fullSnapshotEstimate}
                  prPreflight={{
                    ...preflight,
                    mode: "verified_full_snapshot",
                    reason: "explicit_full_snapshot",
                    counts: {
                      ...preflight.counts,
                      filesToAnalyze: preflight.fullSnapshotEstimate.selectedFiles,
                      toAnalyze: preflight.fullSnapshotEstimate.selectedFiles,
                      modelAnalyzedFiles: preflight.fullSnapshotEstimate.selectedFiles,
                    },
                  }}
                  confirmLabel="Confirm full snapshot"
                  onConfirm={() => handleConfirm("verified_full_snapshot")}
                  onCancel={() => setShowFullSnapshot(false)}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
